import * as Mutations from "./mutation-types";
import * as Actions from "./action-types";
import * as api from "@/api/api";
import * as utils from "@/api/utils";
import * as Helpers from "@/helpers";
import { executeQueryHelper } from "@/store/CommonStoreHelpers";
import _ from "lodash";

const defaultChannelsQuery = {
  start_date: "",
  end_date: "",
  displayColumns: [],
  groupby: null,
  weekdays: [],
  reachCriteria: "1",
  channels: {},
  target_group: [],
  dayparts: []
};

const defaultChannelsRoFQuery = {
  keys: [],
  return_type: "absolute",
  aggregation_type: "cumulative",
  reachCritera: "1",
  max_frequencies: 10
};

const state = {
  hasSetDefaultChannelsQuery: false,
  queryCost: {
    data: null,
    isLoading: false,
    error: null
  },
  channelsInitStatus: "NOT_LOADED",
  channelsQuery: _.cloneDeep(defaultChannelsQuery),
  defaultChannelsQuery: _.cloneDeep(defaultChannelsQuery),
  channelsRoFQuery: {
    ...defaultChannelsRoFQuery
  },
  channelsQueryForAPI: {},
  channelsResults: {
    status: "NOT_LOADED"
  },
  channelsRoFResults: {
    status: "NOT_LOADED"
  },
  channelsSummary: [],
  channelsColumnTypes: [],
  channelsDayparts: [],
  channelsTimeshift: [],
  channelsColumnFieldsOptions: [],
  channelsAllWithStarSelected: true,
  channelsRoFResultsLoadDetails: false,
  channelsSumVisible: false,
  channelsChannelHouses: [],
  channelsMeasures: [],
  channelsExcludedChannels: []
};
const convertFromQueryToQueryForAPI = (
  ctx,
  q,
  key_column,
  options = {
    sortBy: null,

    sort_direction: "ASC",
    rows_per_page: 10000000
  }
) => {
  let splitDayparts = utils.splitDayParts(q.dayparts);
  // transformera interna queryObjektet till ett som passar vår API
  const defaultChannelsQueryForAPI = {
    rows_per_page: options.rows_per_page,
    sort_by: options.sortBy,
    sort_direction: options.sort_direction,
    columns: [],
    displayColumns: [],
    measures: [],
    min_slot_length: 0,
    filters: null,
    day_parts: splitDayparts,
    orig_codes: q.channels ?
      Object.keys(q.channels).reduce(
        (prev, key) => [...prev, ...q.channels[key]],
        []
      ): [],
    key_column: key_column,
    last_row_values: null,
    search: null,
    tab: "kanaler"
  };

  const defaultMeasure = {
    min_viewing_time: 0
  };
  const defaultColumn = {};
  // är inga groupbycolumns valda så skickar vi med alla kolumner
  var columns = q.columns;
  if (columns.length == 0) {
    columns = ctx.state.channelsColumnTypes.filter(x => x.hidden == false || x.default_selection == true);
  }

  // + skicka alltid med alwaysIncludeInGroupBy, om de inte redan finns
  columns = columns.concat(ctx.state.channelsColumnTypes
    .filter(col => col.alwaysIncludeInGroupBy && !columns.some(c => c.column_type === col.column_type)));

  columns = columns.map(x => ({ column_type: x.column_type, display_name: x.display_name }));


  const APIQuery = {
    ...defaultChannelsQueryForAPI,
    start_date: q.start_date ?? null,
    end_date: q.end_date ?? null,
    min_slot_length: q.duration ? q.duration * 60 : 0,
    reach_criteria: q.reachCriteria,
    measures: q.target_group ?
      q.target_group.map(measure => {
        let targetGroup = {
          group: measure.group == 'W' ? 'K' : measure.group,
          age_from: measure.age_from,
          age_to: measure.age_to
        };
        
        // Add Other target group fields if group is "O"
        if (measure.group === 'O' && measure.othertargetgroups && measure.othertargetgroupsvalues) {
          targetGroup.other_category = measure.othertargetgroups;
          targetGroup.other_value = measure.othertargetgroupsvalues;
        }
        
        return {
          ...defaultMeasure,
          min_viewing_time: measure.reach_criteria,
          target_group: targetGroup,
          consolidated: measure.timeshift.consolidated,
          days: measure.timeshift.days,
          timeshift: measure.timeshift.keys,
          display_name: Helpers.createMeasureName(measure),
          column_type: measure.measure.column_type
        };
      }): [],
    columns: columns
      ? columns.map(column => ({
        ...defaultColumn,
        ...column
      }))
      : [],
    displayColumns: q.displayColumns
      ? q.displayColumns.map(column => ({
              ...defaultColumn,
              ...column
      })): [],
    weekday_numbers: q.weekdays
  };

  return APIQuery;
};

const executeRoFQuery = async (context, query) => {
  try {
    var r = await api.executeChannelsRoFQuery(query);
    // fått ett svar
    // kolla om vi fått några rader
    if (!(r.keys && r.keys.length > 0)) {
      context.commit(Mutations.setChannelsRoFResults, {
        status: "EMPTY_RESULT"
      });
    } else {
      // spara resultaten formaterat enligt query

      const formatFunc = val =>
        query.return_type == "absolute" // absoluta värden visas i tusental och avrundat
        ?
        Math.round(val / 1000, 0).toLocaleString("sv-Sv") // relativa värden visas i procen med 1 decimal
          :
          `${Math.round(val * 1000) / 10}`;

      context.commit(Mutations.setChannelsRoFResults, {
        status: "LOADED",
        data: {
          ...r,
          // formatera frekvensmatrisen
          frequency_matrix: r.frequency_matrix.map(row =>
            row.map(cell => formatFunc(cell))
          ),
          reach_1_plus_acc: r.reach_1_plus_acc.map(cell => formatFunc(cell)),
          average_frquency: r.average_frquency.map(cell => cell.toFixed(1).replace('.', ',')),
          rating: r.rating.map(cell => formatFunc(cell))
        }
      });
    }
  } catch (error) {
    context.commit(Mutations.setChannelsRoFResults, {
      status: "FAILED",
      data: null,
      error: error
    });
  }
};

// hjälpfunktion för att exekvera kanalfrågan
const executeQuery = async (context, query, addRows = false) => {
  await executeQueryHelper(context, query, {
    addRows,
    moduleName: "Channels",
    mutations: Mutations,
    getters: context.getters,
    executeQueryFn: api.executeQueryDataQuery
  });
};

const PrepareQuery = (context, showGroupByDropdowns) => {
  const query = context.state.channelsQueryForAPI;
  // om vi inte renderat datat, så finns inte dessa dropdowns
  let aggregationTypeDropdowns =
    context.getters.getChannelsResults?.data?.aggregationTypeDropdowns;
  if (aggregationTypeDropdowns != null) {
    query.measures = query.measures.map(measure => ({
      ...measure,
      default_aggregation: showGroupByDropdowns ?
        aggregationTypeDropdowns
        .find(t => t.key === measure.display_name)
          .value.toUpperCase() : "AVG"
    }));
  }
  query.sort_by = context.state.channelsQueryForAPI.sort_by;
  query.sort_direction = context.state.channelsQueryForAPI.sort_direction;
  query.excludedRows = context.state.channelsExcludedChannels;
  query.last_row_values = null;
  query.showGroupByDropdowns = showGroupByDropdowns;
  return query;
}

const GetRoFQuery = context => {
  // konvertera till fråga för API
  const query = {
    keys: context.state.channelsRoFQuery.keys.map(key => JSON.parse(key)),
    panel_date: context.state.channelsRoFQuery.panel_date,
    reach_criteria: context.state.channelsRoFQuery.reach_criteria,
    return_type: context.state.channelsRoFQuery.return_type,
    aggregation_type: context.state.channelsRoFQuery.aggregation_type,
    max_frequencies: context.state.channelsRoFQuery.max_frequencies,
    target_group: context.state.channelsRoFQuery.target_group_rof,
    start_date: context.state.channelsRoFQuery.start_date,
    end_date: context.state.channelsRoFQuery.end_date
  };
  if(query.target_group.group === 'W') 
    query.target_group.group = 'K';
  
  return query;
};

const actions = {
  // hämta alla data som krävs för att visa sidan

  [Actions.getChannelsColumnTypes]: async () => {
    return await api.getColumnTypes("CHANNELS");
  },
  [Actions.getChannelsMeasures]: async () => {
    return await api.getMeasures("CHANNELS");
  },
  [Actions.getChannelsChannels]: async () => {
    let dateQuery = {
      start_date: state.channelsQuery.start_date,
      end_date: state.channelsQuery.end_date
    };

    return await api.getAllChannels(dateQuery);
  },

  [Actions.getChannelsTimeshift]: async () => {
    const timeshift = await api.getTimeshift();
    // här tar vi just nu bara de för aggregerade timeshifts,
    // alltså KO, OV, LIVE osv. och grupperar ihop
    return timeshift;
    //await context.commit(Mutations.setChannelsTimeshift, groupedTimeshift)
  },

  // denna initialiserar tabellen och frågan, alltså använda då tryck på 'Sök'
  [Actions.executeChannelsQuery]: async context => {
    context.commit(Mutations.setChannelsResults, {
      status: "LOADING",
      data: null
    });

    // initiera/resetta selected rows
    context.commit(Mutations.setChannelsExcludedChannels, []);

    const selColumns = context.getters["getChannelsQuery"].columns;
    const columnFieldsOptions =
      context.getters["getChannelsColumnFieldsOptions"];
    const allStarSelected = Helpers.allWithStarsSelected(
      selColumns,
      columnFieldsOptions
    );

    context.commit(Mutations.setChannelsAllWithStarSelected, allStarSelected);



    // ifall allstarselected, lägg till alla key columns som behövs
    let key_column = null;
    if (allStarSelected || context.state.channelsQuery.columns.length == 0) {
      key_column = context.state.channelsColumnTypes
        .filter(c => c.key == true)
        .map(col => ({
          column_type: col.column_type,
          display_name: col.display_name
        }))[0];
    }


    // konvertera till fråga för API
    const columnsWithoutKey = context.state.channelsColumnTypes
      .filter(col => !col.key)
      .map(x => x.display_name);

    context.state.channelsQuery.columns.sort(function (a, b) {
      return (
        columnsWithoutKey.indexOf(a.display_name) -
        columnsWithoutKey.indexOf(b.display_name)
      );
    });

    const query = convertFromQueryToQueryForAPI(
      context,
      context.state.channelsQuery,
      key_column
    );
    // kolla om frågan är OK att köra och returnera till gränssnittet, annars sätt queryCost så tar vi det därifrån
    context.commit(Mutations.setQueryCostLoading, true);
    context.commit(Mutations.setQueryCostError, null);

    try {
      const queryCostData = await api.getQueryCost(query);
      await context.commit(Mutations.setQueryCostData, queryCostData);
    } catch (error) {
      context.commit(Mutations.setChannelsResults, {
        status: "VALIDATION_FAILED"
      });
      await context.commit(Mutations.setQueryCostError, error.response.data.message);
      return;
    } finally {
      await context.commit(Mutations.setQueryCostLoading, false);
    }

    // comitta queryn till store (så att den används vid exporten)
    await context.commit(Mutations.setChannelsQueryForAPI, query);

    if (
      context.state.queryCost.error == null &&
      context.state.queryCost.data.queryClass == "Normal"
    ) {
      await executeQuery(context, query, false);
    } else {
      context.commit(Mutations.setChannelsResults, {
        status: "NOT_LOADING",
        data: null
      });
    }
  },
  [Actions.executeScheduleQuery]: async (context, reportname) => {
    let query = PrepareQuery(context, false);
    await api.createScheduledReportQuery(reportname, query);
  },
  [Actions.executeExportChannelsQuery]: async (
    context,
    showGroupByDropdowns
  ) => {
    let query = PrepareQuery(context, showGroupByDropdowns);
    return await api.executeExportQuery(query);
  },

  [Actions.executeChannelsRoFQuery]: async context => {
    context.commit(Mutations.setChannelsRoFResults, {
      status: "LOADING",
      data: null
    });

    const query = GetRoFQuery(context);
    await executeRoFQuery(context, query);
  },
  [Actions.executeChannelsQueryMoreExclude]: async (
    context,
    rowsWithoutExcludedRows
  ) => {
    context.commit(Mutations.setChannelsResults, {
      ...context.state.channelsResults,
      data: {
        ...context.state.channelsResults.data,
        rows: rowsWithoutExcludedRows
      },
      loadingTable: false
    });
  },
  [Actions.changeSortChannels]: async (context, payload) => {
    let columnInfo = context.getters.getChannelsResults.data.columns.find(
      c => c.key == payload.sortBy
    );
    let tableRows = [
      ...context.getters.getChannelsResults.data.rows.filter(p => !p.type)
    ];
    if (columnInfo.isNumber) {
      if (payload.sortDesc)
        tableRows.sort((a, b) => {
          let first = a[payload.sortBy];
          let second = b[payload.sortBy];
          if (typeof first === "string")
            first = parseFloat(first.replace(/\s/g, "").replace(/,/g, "."));
          if (typeof second === "string")
            second = parseFloat(second.replace(/\s/g, "").replace(/,/g, "."));
          return second - first;
        });
      else {
        tableRows.sort((a, b) => {
          let first = a[payload.sortBy];
          let second = b[payload.sortBy];
          if (typeof first === "string")
            first = parseFloat(first.replace(/\s/g, "").replace(/,/g, "."));
          if (typeof second === "string")
            second = parseFloat(second.replace(/\s/g, "").replace(/,/g, "."));
          return first - second;
        });
      }
    } else if (columnInfo.isPercentage) {
      if (payload.sortDesc) {
        tableRows.sort((a, b) => {
          a = parseFloat(
            a[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          b = parseFloat(
            b[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          if (a < b) {
            return 1;
          }
          if (a > b) {
            return -1;
          }
          return 0;
        });
      } else {
        tableRows.sort((a, b) => {
          a = parseFloat(
            a[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          b = parseFloat(
            b[payload.sortBy].replace(/,/g, ".").replace(/[\s%]/g, "")
          );
          if (a < b) {
            return -1;
          }
          if (a > b) {
            return 1;
          }
          return 0;
        });
      }
    } else if (columnInfo.isWeekday) {
      utils.sortAsWeekday(tableRows, payload);
    } else {
      if (payload.sortDesc) {
        tableRows.sort((a, b) => {
          a = a[payload.sortBy];
          b = b[payload.sortBy];
          if (a < b || a == null) {
            return 1;
          }
          if (a > b || b == null) {
            return -1;
          }
          return 0;
        });
      } else {
        tableRows.sort((a, b) => {
          a = a[payload.sortBy];
          b = b[payload.sortBy];
          if (a < b || a == null) {
            return -1;
          }
          if (a > b || b == null) {
            return 1;
          }
          return 0;
        });
      }
    }
    let summaryArray = context.getters.getChannelsResults.data.summary.array.map(
      obj => {
        return {
          ...obj,
          visible: false
        };
      }
    );
    tableRows.unshift(
      ...context.getters.getChannelsResults.data.rows.filter(p => p.type)
    );
    context.commit(Mutations.setChannelsResults, {
      ...state.channelsResults,
      data: {
        ...context.state.channelsResults.data,
        rows: tableRows,
        summary: {
          ...context.state.channelsResults.data.summary,
          array: summaryArray
        }
      },
      loadingTable: false
    });

    const sortDir = payload.sortDesc === true ? "DESC" : "ASC";
    // skapa kolumnobjekt från data-objektet
    const column = context.getters.getChannelsResults.data.columns.filter(
      c => c.key == payload.sortBy
    )[0];
    const sortByColumn = (({
          column_type,
          key
        }) => ({
      column_type,
      display_name: key
    }))(column);
    context.commit(Mutations.setChannelsQueryForAPI, {
      ...context.getters.getChannelsQueryForAPI,
      sort_by: sortByColumn,
      sort_direction: sortDir
    });
  }
};

const getters = {
  getHasSetDefaultChannelsQuery: () => state.hasSetDefaultChannelsQuery,
  getDefaultChannelsQuery: () => state.defaultChannelsQuery,
  getChannelsExcludedChannels: state => state.channelsExcludedChannels,
  getChannelsQuery: state => state.channelsQuery,
  getChannelsRoFQuery: state => state.channelsRoFQuery,
  getChannelsResults: state => state.channelsResults,
  getChannelsRoFResults: state => state.channelsRoFResults,
  getChannelsSummary: state => state.channelsSummary,
  getChannelsQueryForAPI: state => state.channelsQueryForAPI,
  getChannelsColumnTypes: ({
      channelsColumnTypes
    }) => channelsColumnTypes,
    getChannelsChannels: ({
      channelsChannels
    }) => channelsChannels,
    getChannelsChannelHouses: ({
      channelsChannelHouses
    }) =>
    channelsChannelHouses,
  getChannelsInitStatus: ({
    channelsInitStatus
  }) => channelsInitStatus,
  getChannelsColumnFieldsOptions: state => state.channelsColumnFieldsOptions,
  getChannelsAllWithStarSelected: state => state.channelsAllWithStarSelected,
  getChannelsRoFResultsLoadDetails: state =>
    state.channelsRoFResultsLoadDetails,
  getChannelsSumVisible: state => state.channelsSumVisible,
  getChannelsQueryCost: state => state.queryCost,
  getChannelsMeasures: state => state.channelsMeasures
};

const mutations = {
  [Mutations.setHasSetDefaultChannelsQuery]: (state, payload) =>
    (state.hasSetDefaultChannelsQuery = payload),
  [Mutations.setDefaultChannelsQuery]: (state, payload) =>
    (state.defaultChannelsQuery = payload),
  [Mutations.setChannelsExcludedChannels]: (state, payload) =>
    (state.channelsExcludedChannels = payload),
  [Mutations.setChannelsQuery]: (state, payload) =>
    (state.channelsQuery = payload),
  [Mutations.setChannelsRoFQuery]: (state, payload) =>
    (state.channelsRoFQuery = payload),
  [Mutations.setChannelsResults]: (state, payload) =>
    (state.channelsResults = payload),
  [Mutations.setChannelsResultsColumns]: (state, payload) =>
    (state.channelsResults.data.columns = payload),
  [Mutations.setChannelsSummary]: (state, payload) =>
    (state.channelsSummary = payload),
  [Mutations.setChannelsColumnTypes]: (state, payload) =>
    (state.channelsColumnTypes = payload),
  [Mutations.setChannelsInitStatus]: (state, payload) =>
    (state.channelsInitStatus = payload),
  [Mutations.setChannelsQueryForAPI]: (state, payload) =>
    (state.channelsQueryForAPI = payload),
  [Mutations.setChannelsTimeshift]: (state, payload) =>
    (state.channelsTimeshift = payload),
  [Mutations.setChannelsChannels]: (state, payload) =>
    (state.channelsChannels = payload),
  [Mutations.setChannelsChannelHouses]: (state, payload) =>
    (state.channelsChannelHouses = payload),
  [Mutations.setChannelsRoFResults]: (state, payload) =>
    (state.channelsRoFResults = payload),
  [Mutations.setChannelsDayparts]: (state, payload) =>
    (state.channelsDayparts = payload),
  [Mutations.setChannelsStartdate]: (state, payload) =>
    (state.channelsQuery.start_date = payload),
  [Mutations.setChannelsEnddate]: (state, payload) =>
    (state.channelsQuery.end_date = payload),
  [Mutations.setChannelsDisplayColumnFieldsOptions]: (state, payload) =>
    (state.channelsDisplayColumnFieldsOptions = payload),
  [Mutations.setChannelsColumnFieldsOptions]: (state, payload) =>
    (state.channelsColumnFieldsOptions = payload),
  [Mutations.setChannelsAllWithStarSelected]: (state, payload) =>
    (state.channelsAllWithStarSelected = payload),
  [Mutations.setChannelsRoFResultsLoadDetails]: (state, payload) =>
    (state.channelsRoFResultsLoadDetails = payload),
  [Mutations.setChannelsSumVisible]: (state, payload) =>
    (state.channelsSumVisible = payload),
  [Mutations.setChannelsGroupBy]: (state, payload) =>
    (state.channelsQuery.groupby = payload),
  [Mutations.setQueryCostData]: (state, payload) =>
    (state.queryCost.data = payload),
  [Mutations.setQueryCostLoading]: (state, payload) =>
    (state.queryCost.loading = payload),
  [Mutations.setQueryCostError]: (state, payload) =>
    (state.queryCost.error = payload),
  [Mutations.setChannelsMeasures]: (state, payload) =>
    (state.channelsMeasures = payload)
};

export default {
  state,
  getters,
  mutations,
  actions
};